import { inject, Injectable } from '@angular/core';
import { CheckPointService } from '../services/checkPoint.service';
import { DatabaseService } from '../services/database.service';
import { FormDefinitionService } from '../services/form-definition.service';
import { FormService } from '../services/form.service';
import { LoggerService } from '../services/logger.service';
import { TranslationService } from '../services/translation.service';
import { UserService } from '../services/user.service';
import {
  ReportCellValue,
  ReportColumn,
  ReportDefinition,
  ReportResult,
  ReportResultColumn,
} from '../models/report.model';
import { getReportableDynamicFields } from './report-field.utils';
import { buildReportQuery, effectiveColumns } from './report-query.builder';

/**
 * Executes a {@link ReportDefinition} against the local SQLite replica and maps
 * the raw rows into a display-ready {@link ReportResult}:
 * - metadata id columns (checkpoint, created_by, form) are resolved to names
 * - dynamic enum values are resolved to their option labels
 * - `created_at` is formatted to a readable local timestamp
 *
 * All execution is local and offline-first — no network calls.
 */
@Injectable({ providedIn: 'root' })
export class ReportQueryService {
  private db = inject(DatabaseService);
  private checkpointService = inject(CheckPointService);
  private userService = inject(UserService);
  private formService = inject(FormService);
  private formDefinitionService = inject(FormDefinitionService);
  private translate = inject(TranslationService);
  private log = inject(LoggerService).getLogger('report-query');

  async execute(def: ReportDefinition): Promise<ReportResult> {
    const built = buildReportQuery(def);
    this.log.debug('Executing report query', { sql: built.sql });

    const rows = await this.db.exec(built.sql, built.params as unknown[]);

    if (built.isGrouped) {
      return this.mapGroupedResult(def, rows);
    }
    if (built.isAggregate) {
      return this.mapAggregateResult(def, rows);
    }
    return this.mapDetailResult(def, rows);
  }

  private aggregateLabel(def: ReportDefinition): string {
    return this.translate.translate('reports.agg.' + def.aggregation.mode);
  }

  private mapAggregateResult(def: ReportDefinition, rows: any[]): ReportResult {
    const rawValue = rows.length > 0 ? rows[0].agg_value : null;
    const value = rawValue == null ? null : Number(rawValue);

    return {
      columns: [{ key: 'agg_value', label: this.aggregateLabel(def), fieldType: 'number' }],
      rows: [{ agg_value: value }],
      summary: {
        rowCount: def.aggregation.mode === 'count' && value != null ? value : 1,
        aggregateValue: value,
      },
    };
  }

  /** Maps a grouped aggregate: one row per group with a resolved group label. */
  private async mapGroupedResult(def: ReportDefinition, rows: any[]): Promise<ReportResult> {
    const group = def.groupBy!;
    const groupColumn: ReportColumn = {
      source: group.source,
      key: group.key,
      label: group.label,
      // group label resolution reuses the metadata/enum resolvers
      fieldType: group.source === 'metadata' ? 'text' : 'enum',
    };
    const resolvers = await this.buildResolvers(def, [groupColumn]);

    const resultColumns: ReportResultColumn[] = [
      { key: 'group_key', label: this.columnLabel(groupColumn), fieldType: groupColumn.fieldType },
      { key: 'agg_value', label: this.aggregateLabel(def), fieldType: 'number' },
    ];

    const resultRows = rows.map((row) => ({
      group_key: this.resolveCell(groupColumn, row.group_key, resolvers),
      agg_value: row.agg_value == null ? null : Number(row.agg_value),
    }));

    return {
      columns: resultColumns,
      rows: resultRows,
      summary: { rowCount: resultRows.length },
    };
  }

  private async mapDetailResult(def: ReportDefinition, rows: any[]): Promise<ReportResult> {
    const columns = effectiveColumns(def);
    const resolvers = await this.buildResolvers(def, columns);

    const resultColumns: ReportResultColumn[] = columns.map((col, i) => ({
      key: `c${i}`,
      label: this.columnLabel(col),
      fieldType: col.fieldType,
    }));

    const resultRows = rows.map((row) => {
      const mapped: Record<string, ReportCellValue> = {};
      columns.forEach((col, i) => {
        mapped[`c${i}`] = this.resolveCell(col, row[`c${i}`], resolvers);
      });
      return mapped;
    });

    return {
      columns: resultColumns,
      rows: resultRows,
      summary: { rowCount: resultRows.length },
    };
  }

  /**
   * Loads the label/value lookups needed to render a read-only summary of a
   * report's criteria (filters, aggregation target, group-by) on the results
   * page — same underlying data as {@link buildResolvers}, but keyed off the
   * definition's filters/aggregation/groupBy rather than its output columns,
   * and always includes dynamic field *labels* (not just enum options),
   * since criteria display needs a field's name regardless of its type.
   */
  async loadCriteriaResolvers(def: ReportDefinition): Promise<CriteriaResolvers> {
    const needsCheckpoint = def.metadataFilters.some((f) => f.field === 'checkpoint');
    const needsUser = def.metadataFilters.some((f) => f.field === 'created_by');
    const needsDynamic =
      def.dynamicFilters.length > 0 ||
      def.aggregation.targetSource === 'dynamic' ||
      def.groupBy?.source === 'dynamic';

    const [checkpoints, users, forms] = await Promise.all([
      needsCheckpoint ? this.checkpointService.getAll() : Promise.resolve([]),
      needsUser ? this.userService.getAll() : Promise.resolve([]),
      needsDynamic && def.formId ? this.formService.getAll() : Promise.resolve([]),
    ]);

    const enumOptions = new Map<string, Map<string, string>>();
    const dynamicFieldLabels = new Map<string, string>();
    const definitionId = forms.find((f) => f.id === def.formId)?.form_definition_id;
    if (needsDynamic && definitionId) {
      const fields = await this.formDefinitionService.getFormDefinition(definitionId);
      for (const f of getReportableDynamicFields(fields)) {
        dynamicFieldLabels.set(f.key, f.label);
        if (f.fieldType === 'enum' && f.options) {
          enumOptions.set(f.key, new Map(f.options.map((o) => [String(o.value), o.label])));
        }
      }
    }

    return {
      checkpointNames: new Map(checkpoints.map((c) => [c.id ?? '', c.name])),
      userNames: new Map(users.map((u) => [u.id, u.full_name])),
      enumOptions,
      dynamicFieldLabels,
    };
  }

  /** Human label for a column: translated metadata name or the field's label. */
  private columnLabel(col: ReportColumn): string {
    if (col.label) return col.label;
    if (col.source === 'metadata') {
      return this.translate.translate(METADATA_LABEL_KEY[col.key] ?? col.key);
    }
    return col.key;
  }

  /** Loads the lookup maps needed to resolve ids/enums to human labels. */
  private async buildResolvers(
    def: ReportDefinition,
    columns: ReportColumn[],
  ): Promise<CellResolvers> {
    const needs = {
      checkpoint: columns.some((c) => c.source === 'metadata' && c.key === 'checkpoint'),
      user: columns.some((c) => c.source === 'metadata' && c.key === 'created_by'),
      form: columns.some((c) => c.source === 'metadata' && c.key === 'form'),
      instance: columns.some((c) => c.source === 'metadata' && c.key === 'asset_instance'),
      enum: columns.some((c) => c.source === 'dynamic' && c.fieldType === 'enum'),
    };

    const [checkpoints, users, forms, instances] = await Promise.all([
      needs.checkpoint ? this.checkpointService.getAll() : Promise.resolve([]),
      needs.user ? this.userService.getAll() : Promise.resolve([]),
      needs.form || needs.enum ? this.formService.getAll() : Promise.resolve([]),
      needs.instance
        ? this.db.exec('SELECT id, serial_number FROM asset_instances')
        : Promise.resolve([]),
    ]);

    const enumOptions = new Map<string, Map<string, string>>();
    const definitionId = forms.find((f) => f.id === def.formId)?.form_definition_id;
    if (needs.enum && definitionId) {
      const fields = await this.formDefinitionService.getFormDefinition(definitionId);
      for (const f of getReportableDynamicFields(fields)) {
        if (f.fieldType === 'enum' && f.options) {
          enumOptions.set(
            f.key,
            new Map(f.options.map((o) => [String(o.value), o.label])),
          );
        }
      }
    }

    return {
      checkpointNames: new Map(checkpoints.map((c) => [c.id ?? '', c.name])),
      userNames: new Map(users.map((u) => [u.id, u.full_name])),
      formNames: new Map(forms.map((f) => [f.id ?? '', f.name])),
      instanceSerials: new Map(
        instances.map((row) => [String(row.id), String(row.serial_number ?? '')]),
      ),
      enumOptions,
    };
  }

  /** Resolves a single raw cell value to its display value. */
  private resolveCell(
    col: ReportColumn,
    raw: unknown,
    resolvers: CellResolvers,
  ): ReportCellValue {
    if (raw == null) return null;

    if (col.source === 'metadata') {
      switch (col.key) {
        case 'checkpoint':
          return resolvers.checkpointNames.get(String(raw)) ?? String(raw);
        case 'created_by':
          return resolvers.userNames.get(String(raw)) ?? String(raw);
        case 'form':
          return resolvers.formNames.get(String(raw)) ?? String(raw);
        case 'asset_instance': {
          // '' means the record isn't for a specific copy/unit.
          if (raw === '') return null;
          const serial = resolvers.instanceSerials.get(String(raw));
          return serial ? `#${serial}` : String(raw);
        }
        case 'created_at':
          return formatTimestamp(Number(raw));
        default:
          return normalizeCell(raw);
      }
    }

    if (col.fieldType === 'boolean') {
      // json_extract() yields 1/0 for JSON true/false.
      const truthy = raw === true || raw === 1 || raw === '1' || raw === 'true';
      return this.translate.translate(truthy ? 'reports.filters.boolean.true' : 'reports.filters.boolean.false');
    }

    if (col.fieldType === 'enum') {
      // Multi-select values (checkbox-group) come back from json_extract as JSON
      // array text, not an array.
      const value = parseJsonArray(raw) ?? raw;
      const optionMap = resolvers.enumOptions.get(col.key);
      if (Array.isArray(value)) {
        return value.map((v) => optionMap?.get(String(v)) ?? String(v)).join(', ');
      }
      if (optionMap) {
        return optionMap.get(String(value)) ?? String(value);
      }
    }

    return normalizeCell(raw);
  }
}

interface CellResolvers {
  checkpointNames: Map<string, string>;
  userNames: Map<string, string>;
  formNames: Map<string, string>;
  /** asset_instances.id → serial_number, for the asset_instance column. */
  instanceSerials: Map<string, string>;
  enumOptions: Map<string, Map<string, string>>;
}

/** Label/value lookups for the read-only criteria summary (see {@link ReportQueryService.loadCriteriaResolvers}). */
export interface CriteriaResolvers {
  checkpointNames: Map<string, string>;
  userNames: Map<string, string>;
  enumOptions: Map<string, Map<string, string>>;
  dynamicFieldLabels: Map<string, string>;
}

/** i18n keys for metadata field labels — shared by column and criteria label resolution. */
export const METADATA_LABEL_KEY: Record<string, string> = {
  checkpoint: 'reports.field.checkpoint',
  form: 'reports.field.form',
  asset_instance: 'reports.field.assetInstance',
  created_by: 'reports.field.createdBy',
  created_at: 'reports.field.createdAt',
};

/** Coerces a raw JSON value into a flat displayable cell value. */
function normalizeCell(raw: unknown): ReportCellValue {
  if (raw == null) return null;
  if (typeof raw === 'boolean' || typeof raw === 'number' || typeof raw === 'string') {
    return raw;
  }
  if (Array.isArray(raw)) return raw.map((v) => String(v)).join(', ');
  return JSON.stringify(raw);
}

/** Returns the array encoded in a JSON-array string (e.g. `'["a","b"]'`), else null. */
function parseJsonArray(raw: unknown): unknown[] | null {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string' || !raw.startsWith('[')) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Formats an epoch-millis timestamp to a readable `YYYY-MM-DD HH:mm` string. */
function formatTimestamp(ms: number): string {
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
